import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Image,
  Button,
  VStack,
  HStack,
  Text,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import Breadcrumb from "../components/Breadcrumb";
import {
  getCategoryBySlug,
  getCategoryName,
  getPrintTitle,
} from "../data/printsData";

const CONTACT_EMAIL = "algranoconpablo@gmail.com";

const PrintCategory = () => {
  const { category: categorySlug, lng } = useParams<{ category: string; lng: string }>();
  const { t } = useTranslation('navigation');
  const { t: tPrints } = useTranslation('prints');
  const currentLanguage = lng || 'en';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<string>("");

  const category = getCategoryBySlug(categorySlug || '');

  const handleImageClick = (imagePath: string) => {
    setSelectedImage(imagePath);
    onOpen();
  };

  const getLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };

  if (!category) {
    return (
      <Box
        maxWidth="1400px"
        margin="0 auto"
        padding="20px"
        paddingTop={{ base: "80px", md: "20px" }}
      >
        <Text>Category not found</Text>
      </Box>
    );
  }

  // Pre-fills the email so an enquiry arrives already identifying the print
  const getContactLink = (printTitle: string) => {
    const subject = tPrints('contact.emailSubject', { print: printTitle });
    const body = tPrints('contact.emailBody', { print: printTitle });
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Box
      maxWidth="1400px"
      margin="0 auto"
      padding="20px"
      paddingTop={{ base: "80px", md: "20px" }}
      pr="5%"
      pl="5%"
    >
      {/* Breadcrumb */}
      <Box mb={6}>
        <Breadcrumb
          items={[
            { label: t('breadcrumbs.work'), href: getLocalizedPath('/') },
            { label: t('breadcrumbs.prints'), href: getLocalizedPath('/prints') },
            { label: getCategoryName(category, currentLanguage) }
          ]}
        />
      </Box>

      {/* Prints Grid */}
      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        gap={8}
      >
        {category.prints.map((print) => {
          const printTitle = getPrintTitle(category, print, currentLanguage);

          return (
            <VStack
              key={print.id}
              spacing={4}
              align="stretch"
              border="4px solid black"
              padding={4}
              bg="white"
              _hover={{ transform: "translateY(-2px)", transition: "0.2s" }}
            >
              {/* Print Image */}
              <Box
                width="100%"
                overflow="hidden"
                bg="gray.100"
                cursor="pointer"
                onClick={() => handleImageClick(print.imagePath)}
                _hover={{ opacity: 0.9, transition: "opacity 0.2s" }}
              >
                <Image
                  src={print.imagePath}
                  alt={printTitle}
                  width="100%"
                  height="auto"
                  objectFit="cover"
                  loading="lazy"
                />
              </Box>

              <Text
                fontFamily="Source Code Pro"
                fontSize="md"
                fontWeight="bold"
              >
                {printTitle}
              </Text>

              {/* Available sizes and prices */}
              <VStack spacing={1} align="stretch">
                {print.sizes.map((sizeOption) => (
                  <HStack
                    key={`${print.id}-${sizeOption.size}`}
                    justifyContent="space-between"
                  >
                    <Text fontFamily="Source Code Pro" fontSize="sm">
                      {sizeOption.size}
                    </Text>
                    <Text fontFamily="Source Code Pro" fontSize="sm" fontWeight="medium">
                      €{sizeOption.price}
                    </Text>
                  </HStack>
                ))}
              </VStack>

              <Divider borderColor="black" />

              {/* Contact to purchase */}
              <Button
                as="a"
                href={getContactLink(printTitle)}
                width="100%"
                border="2px solid black"
                borderRadius={0}
                bg="white"
                fontFamily="Source Code Pro"
                fontWeight="bold"
                whiteSpace="normal"
                height="auto"
                py={3}
                _hover={{ bg: "black", color: "white" }}
              >
                {tPrints('contact.button')}
              </Button>
            </VStack>
          );
        })}
      </Grid>

      {/* Purchase info */}
      <Box mt={12} border="4px solid black" padding={6} bg="white">
        <VStack align="start" spacing={3}>
          <Text fontFamily="Abel" fontSize="2xl" fontWeight="bold">
            {tPrints('contact.title')}
          </Text>
          <Text fontFamily="Source Code Pro" fontSize="sm">
            {tPrints('contact.description')}
          </Text>
          <Text fontFamily="Source Code Pro" fontSize="sm">
            {tPrints('contact.shipping')}
          </Text>
          <Button
            as="a"
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(tPrints('contact.generalSubject'))}`}
            border="2px solid black"
            borderRadius={0}
            bg="white"
            fontFamily="Source Code Pro"
            fontWeight="bold"
            _hover={{ bg: "black", color: "white" }}
          >
            {CONTACT_EMAIL}
          </Button>
        </VStack>
      </Box>

      {/* Image Zoom Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton
            color="white"
            size="lg"
            _hover={{ bg: "whiteAlpha.200" }}
            zIndex={2}
          />
          <ModalBody
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={{ base: 4, md: 8 }}
          >
            <Image
              src={selectedImage}
              alt="Print preview"
              maxH="90vh"
              maxW="90vw"
              objectFit="contain"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PrintCategory;
